import moment from 'moment';

/**
 * Times of inquiries stored before the fixes of 12.10.2026 (retest KS022): substitutes asked later had no requestedAt,
 * and the revokedAt of an older confirmation was overwritten by the next save (= confirmedAt of the next confirmation
 * + a few ms). Pure, checked in tests/substitutions/overview.check.ts.
 */
type Time = Date | string | null | undefined;

/** Creation time of a Mongo ObjectId (string, ObjectId or populated document) */
export function objectIdTime(value: unknown): Date | undefined {
  const raw = value && typeof value === 'object' && '_id' in (value as object) ? (value as { _id: unknown })._id : value;
  const id = raw === undefined || raw === null ? '' : String(raw);
  return /^[0-9a-f]{24}$/i.test(id) ? new Date(parseInt(id.slice(0, 8), 16) * 1000) : undefined;
}

/** When this person was asked: requestedAt, else the time of the sent message (WhatsApp / mail) */
export function requestTimeOf(substitute: { requestedAt?: Time; messengerWhatsAppMessage?: unknown; messengerMail?: unknown }): Date | undefined {
  if (substitute.requestedAt) {
    return moment(substitute.requestedAt).toDate();
  }
  return objectIdTime(substitute.messengerWhatsAppMessage) || objectIdTime(substitute.messengerMail);
}

/** Asked later than the first request ("zusätzlich angefragt"): own requestedAt, or the message is > 10 min younger */
export function askedLater(substitute: { requestedAt?: Time; messengerWhatsAppMessage?: unknown; messengerMail?: unknown }, requestSentAt: Time): boolean {
  if (substitute.requestedAt) {
    return true;
  }
  const at = requestTimeOf(substitute);
  return !!at && !!requestSentAt && moment(at).diff(moment(requestSentAt), 'minutes') > 10;
}

type Confirmation = { confirmedAt?: Time; revokedAt?: Time; updatedAt?: Time };

/**
 * Real time of a revoke / withdrawal: a stored time within 2 s after another confirmation's confirmedAt was overwritten
 * by that save – then the confirmation's updatedAt (if earlier) is the real time. With the withdrawal (rejectedAt) the
 * earliest plausible time wins.
 */
export function revokeTimeOf(confirmation: Confirmation, all: Confirmation[], rejectedAt?: Time): Date | undefined {
  const fix = (time: Time): Date | undefined => {
    if (!time) {
      return undefined;
    }
    const t = moment(time);
    const overwritten = all.some((other) => other !== confirmation && other.confirmedAt
      && t.diff(moment(other.confirmedAt), 'milliseconds') >= 0 && t.diff(moment(other.confirmedAt), 'milliseconds') <= 2000);
    if (overwritten && confirmation.updatedAt && moment(confirmation.updatedAt).isBefore(t)) {
      return moment(confirmation.updatedAt).toDate();
    }
    return t.toDate();
  };
  const times = [fix(rejectedAt), fix(confirmation.revokedAt)].filter(Boolean) as Date[];
  return times.length ? new Date(Math.min(...times.map((d) => d.valueOf()))) : undefined;
}
